import { currentLocale } from '../i18n';
import { localizedError } from '../i18n/errors';
/** Tenký klient nad fetch: cookies vždy, JSON, chyby jako ApiError. */

export interface ApiIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly fieldErrors: Record<string, string>;

  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    issues: ApiIssue[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
    this.fieldErrors = Object.fromEntries(issues.map((i) => [i.path, i.message]));
  }
}

const BASE = (import.meta.env?.VITE_API_URL as string | undefined) ?? '';

/**
 * Jediné API je `/api/v1` (fáze 16). Kód volá `/api/…`, tady se to přepíše na jednom místě;
 * `/api/v1/…` projde beze změny.
 */
export function apiUrl(path: string): string {
  const p =
    path.startsWith('/api/') && !path.startsWith('/api/v1/') ? `/api/v1${path.slice(4)}` : path;
  return BASE + p;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  let res: Response;
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  try {
    res = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      headers: {
        ...(body === undefined || isForm ? {} : { 'content-type': 'application/json' }),
        ...extraHeaders,
      },
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Nepodařilo se navázat spojení se serverem');
  }

  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as {
    error?: { code?: string; message?: string; issues?: ApiIssue[] };
  } | null;
  if (!res.ok) {
    const e = data?.error;
    const code = e?.code ?? 'ERROR';
    throw new ApiError(
      res.status,
      code,
      localizedError(code, e?.message ?? `Chyba ${res.status}`, currentLocale()),
      e?.issues,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  /** POST s vlastními hlavičkami (Idempotency-Key pro offline frontu) */
  postWith: <T>(path: string, body: unknown, headers: Record<string, string>) =>
    request<T>('POST', path, body, headers),
  /** multipart (soubory) – prohlížeč doplní content-type s boundary */
  postForm: <T>(path: string, form: FormData) => request<T>('POST', path, form),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T = undefined>(path: string) => request<T>('DELETE', path),
};
